import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, Mail, Phone, MapPin, Calendar, Briefcase, 
  Car, FileCheck, Users, ArrowRight, Check, HeartHandshake,
  Upload, FileText, AlertCircle, X, Loader2, Sparkles, Truck, 
  ShoppingCart, CheckCircle, Shirt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { applicationsAPI } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';

const WorkerApplication = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  // Dynamic service types from API (Single Source of Truth)
  const [availableServiceTypes, setAvailableServiceTypes] = useState([]);
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(true);
  
  // Fetch active service types on component mount
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        setLoadingServiceTypes(true);
        const response = await api.get('/services/types/active');
        setAvailableServiceTypes(response.data.service_types || []);
      } catch (error) {
        console.error('Failed to fetch service types:', error);
        toast.error('Failed to load available services. Please refresh the page.');
      } finally {
        setLoadingServiceTypes(false);
      }
    };
    fetchServiceTypes();
  }, []);
  
  // Icon mapping for service types
  const getServiceIcon = (iconName) => {
    const icons = {
      'sparkles': <Sparkles className="w-8 h-8" />,
      'shirt': <Shirt className="w-8 h-8" />,
      'truck': <Truck className="w-8 h-8" />,
      'shopping-cart': <ShoppingCart className="w-8 h-8" />,
      'check-circle': <CheckCircle className="w-8 h-8" />,
      'briefcase': <Briefcase className="w-8 h-8" />
    };
    return icons[iconName] || icons['briefcase'];
  };
  
  // Document upload state
  const [documents, setDocuments] = useState({
    id_document: { file: null, path: null, expiration_date: '', uploaded: false },
    drivers_license: { file: null, path: null, expiration_date: '', uploaded: false },
    vehicle_registration: { file: null, path: null, expiration_date: '', uploaded: false },
    auto_insurance: { file: null, path: null, expiration_date: '', uploaded: false },
    background_check: { file: null, path: null, expiration_date: '', uploaded: false }
  });
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    date_of_birth: '',
    ssn_last_four: '',
    service_types: [],
    experience_years: 0,
    certifications: [],
    availability: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    },
    emergency_contact_name: '',
    emergency_contact_phone: '',
    has_transportation: false,
    background_check_consent: false,
    references: [
      { name: '', phone: '', relationship: '' },
      { name: '', phone: '', relationship: '' }
    ]
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.includes(service)
        ? prev.service_types.filter(s => s !== service)
        : [...prev.service_types, service]
    }));
  };

  const handleAvailabilityToggle = (day, time) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: prev.availability[day].includes(time)
          ? prev.availability[day].filter(t => t !== time)
          : [...prev.availability[day], time]
      }
    }));
  };

  const handleReferenceChange = (index, field, value) => {
    setFormData(prev => {
      const newRefs = [...prev.references];
      newRefs[index] = { ...newRefs[index], [field]: value };
      return { ...prev, references: newRefs };
    });
  };

  const handleCertificationToggle = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleDocumentSelect = (docType, e) => {
    // Prevent form submission on mobile
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type and size
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a JPEG, PNG, WebP, or PDF file');
        // Clear the input
        e.target.value = '';
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        // Clear the input
        e.target.value = '';
        return;
      }
      setDocuments(prev => ({
        ...prev,
        [docType]: { ...prev[docType], file, uploaded: false, path: null }
      }));
    }
  };

  const handleExpirationChange = (docType, date) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: { ...prev[docType], expiration_date: date }
    }));
  };

  const uploadDocument = async (docType) => {
    const doc = documents[docType];
    if (!doc.file) {
      toast.error('Please select a file first');
      return;
    }
    if (!doc.expiration_date) {
      toast.error('Please enter an expiration date');
      return;
    }
    
    setUploadingDoc(docType);
    try {
      const response = await applicationsAPI.uploadDocument(
        docType, 
        doc.expiration_date, 
        doc.file
      );
      setDocuments(prev => ({
        ...prev,
        [docType]: { 
          ...prev[docType], 
          uploaded: true, 
          path: response.data.file_path 
        }
      }));
      toast.success(`${getDocumentLabel(docType)} uploaded successfully!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const removeDocument = (docType) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: { file: null, path: null, expiration_date: '', uploaded: false }
    }));
  };

  const getDocumentLabel = (docType) => {
    const labels = {
      id_document: 'ID Document',
      drivers_license: 'Drivers License',
      vehicle_registration: 'Vehicle Registration/Title',
      auto_insurance: 'Auto Insurance',
      background_check: 'Background Check'
    };
    return labels[docType] || docType;
  };

  const areAllDocumentsUploaded = () => {
    // Only require ID document - other documents are optional
    const requiredDocs = ['id_document'];
    return requiredDocs.every(docKey => documents[docKey]?.uploaded && documents[docKey]?.path);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.background_check_consent) {
      toast.error('Please consent to the background check');
      return;
    }
    if (!areAllDocumentsUploaded()) {
      toast.error('Please upload at least your ID document');
      return;
    }
    
    setLoading(true);
    try {
      // Build documents array for submission - only include uploaded docs
      const uploadedDocs = Object.entries(documents)
        .filter(([_, doc]) => doc.uploaded && doc.path)
        .map(([docType, doc]) => ({
          document_type: docType,
          file_path: doc.path,
          expiration_date: doc.expiration_date,
          uploaded_at: new Date().toISOString()
        }));
      
      await applicationsAPI.submit({
        ...formData,
        documents: uploadedDocs
      });
      toast.success('Application submitted successfully!');
      navigate('/application-success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = ['morning', 'afternoon', 'evening'];
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const certifications = ['First Aid', 'Defensive Driving', 'Customer Service', 'Housekeeping Certificate', 'Food Safety'];

  const documentTypes = [
    { key: 'id_document', label: 'ID Document (State ID/Passport)', required: true, icon: FileText },
    { key: 'drivers_license', label: 'Drivers License', required: false, icon: FileCheck },
    { key: 'vehicle_registration', label: 'Vehicle Registration/Title (Proof of Car)', required: false, icon: FileText },
    { key: 'auto_insurance', label: 'Auto Insurance (Proof of Insurance)', required: false, icon: FileCheck },
    { key: 'background_check', label: 'Background Check', required: false, icon: FileText }
  ];

  return (
    <div className="min-h-screen hero-pattern py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <HeartHandshake className="w-8 h-8 text-[var(--primary)]" />
              <span className="text-2xl font-bold gradient-text font-['Cormorant_Garamond']">NC Help Hub</span>
            </Link>
            <h1 className="text-3xl font-bold font-['Cormorant_Garamond'] mb-2">Join Our Team</h1>
            <p className="text-[var(--text-secondary)]">Apply to become a Service Professional</p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                    step >= s ? 'bg-[var(--primary)] text-white' : 'bg-[var(--accent)] text-[var(--text-muted)]'
                  }`}
                >
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 5 && (
                  <div className={`w-12 h-1 ${step > s ? 'bg-[var(--primary)]' : 'bg-[var(--accent)]'}`} />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Information */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
                  <User className="w-5 h-5 text-[var(--primary)]" />
                  Personal Information
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                      data-testid="app-fullname"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                      data-testid="app-email"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                      data-testid="app-phone"
                    />
                  </div>
                  <div>
                    <Label>Date of Birth * (MM/DD/YYYY)</Label>
                    <Input
                      name="date_of_birth"
                      type="text"
                      placeholder="MM/DD/YYYY"
                      value={formData.date_of_birth}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 8) value = value.slice(0, 8);
                        if (value.length >= 2) value = value.slice(0, 2) + '/' + value.slice(2);
                        if (value.length >= 5) value = value.slice(0, 5) + '/' + value.slice(5);
                        setFormData({ ...formData, date_of_birth: value });
                      }}
                      className="input-styled mt-1"
                      required
                      maxLength={10}
                      data-testid="app-dob"
                    />
                  </div>
                </div>

                <div>
                  <Label>Street Address *</Label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="input-styled mt-1"
                    required
                    data-testid="app-address"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <Input
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label>ZIP Code *</Label>
                    <Input
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Last 4 digits of SSN *</Label>
                  <Input
                    name="ssn_last_four"
                    value={formData.ssn_last_four}
                    onChange={handleChange}
                    maxLength={4}
                    className="input-styled mt-1 w-32"
                    required
                    data-testid="app-ssn"
                  />
                </div>

                <Button 
                  type="button" 
                  onClick={() => setStep(2)} 
                  className="btn-primary w-full"
                  data-testid="next-step-1"
                >
                  Continue <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            )}

            {/* Step 2: Professional Information */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
                  <Briefcase className="w-5 h-5 text-[var(--primary)]" />
                  Professional Information
                </h2>

                <div>
                  <Label className="mb-3 block">Service Types * <span className="text-xs text-[var(--text-muted)]">(Select services you can provide)</span></Label>
                  
                  {loadingServiceTypes ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
                      <span className="ml-2 text-[var(--text-muted)]">Loading available services...</span>
                    </div>
                  ) : availableServiceTypes.length === 0 ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-center">
                      <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                      <p className="text-red-700">No services available. Please contact support.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {availableServiceTypes.map((serviceType) => (
                        <label 
                          key={serviceType.code}
                          className={`cursor-pointer card p-4 text-center transition-all ${
                            formData.service_types.includes(serviceType.code) 
                              ? 'border-2 bg-opacity-10' 
                              : ''
                          }`}
                          style={{
                            borderColor: formData.service_types.includes(serviceType.code) ? serviceType.color : undefined,
                            backgroundColor: formData.service_types.includes(serviceType.code) ? `${serviceType.color}15` : undefined
                          }}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={formData.service_types.includes(serviceType.code)}
                            onChange={() => handleServiceToggle(serviceType.code)}
                            data-testid={`service-type-${serviceType.code}`}
                          />
                          <div style={{ color: serviceType.color }} className="mx-auto mb-2">
                            {getServiceIcon(serviceType.icon)}
                          </div>
                          <span className="font-semibold">{serviceType.name}</span>
                          <p className="text-xs text-[var(--text-muted)] mt-1">{serviceType.description.split(' ').slice(0, 3).join(' ')}</p>
                          <p className="text-xs mt-1" style={{ color: serviceType.color }}>
                            {serviceType.service_count} services
                          </p>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {/* Non-Medical Service Disclaimer */}
                  <div className="mt-4 p-4 bg-[var(--success)]/10 border border-[var(--success)]/30 rounded-xl">
                    <p className="text-[var(--success)] text-sm font-medium">
                      <strong>Important:</strong> This is NOT a medical position. NC Help Hub provides <strong>non-medical services only</strong>.
                    </p>
                    {availableServiceTypes.length > 0 && (
                      <ul className="text-green-700 text-xs mt-2 space-y-1 ml-4 list-disc">
                        {availableServiceTypes.map(st => (
                          <li key={st.code}>{st.name} - {st.description}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-green-700 text-xs mt-2 font-medium">
                      No nursing, clinical, or medical duties. No medication handling.
                    </p>
                  </div>
                </div>

                <div>
                  <Label>Years of Experience *</Label>
                  <Input
                    name="experience_years"
                    type="number"
                    min="0"
                    value={formData.experience_years}
                    onChange={handleChange}
                    className="input-styled mt-1 w-32"
                    required
                    data-testid="app-experience"
                  />
                </div>

                <div>
                  <Label className="mb-3 block">Certifications</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {certifications.map((cert) => (
                      <label 
                        key={cert}
                        className={`cursor-pointer p-3 rounded-xl border transition-all text-sm ${
                          formData.certifications.includes(cert) 
                            ? 'border-[var(--primary)] bg-[var(--accent)]' 
                            : 'border-[var(--border-primary)]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={formData.certifications.includes(cert)}
                          onChange={() => handleCertificationToggle(cert)}
                        />
                        <span className="flex items-center gap-2">
                          {formData.certifications.includes(cert) && <Check className="w-4 h-4 text-[var(--primary)]" />}
                          {cert}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Checkbox
                    id="transportation"
                    checked={formData.has_transportation}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, has_transportation: checked }))}
                  />
                  <Label htmlFor="transportation" className="flex items-center gap-2 cursor-pointer">
                    <Car className="w-5 h-5 text-[var(--text-muted)]" />
                    I have reliable transportation
                  </Label>
                </div>

                <div className="flex gap-4">
                  <Button type="button" onClick={() => setStep(1)} className="btn-secondary flex-1">
                    Back
                  </Button>
                  <Button type="button" onClick={() => setStep(3)} className="btn-primary flex-1" data-testid="next-step-2">
                    Continue <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Document Uploads (NEW) */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-bold flex items-center gap-2 mb-2">
                  <Upload className="w-5 h-5 text-[var(--primary)]" />
                  Documents
                </h2>
                <p className="text-[var(--text-secondary)] mb-6">
                  Upload your ID document to proceed. Other documents are optional but recommended.
                </p>

                <div className="p-4 bg-[var(--primary)]/10 border border-[var(--primary)]/30 rounded-xl mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[var(--text-secondary)]">
                      <p className="font-semibold text-[var(--primary)]">Document Requirements:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li><strong>ID Document is required</strong> - State ID or Passport</li>
                        <li>Other documents are optional but may speed up approval</li>
                        <li>Accepted formats: JPEG, PNG, WebP, PDF (max 10MB)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {documentTypes.map((docType) => {
                    const doc = documents[docType.key];
                    const DocIcon = docType.icon;
                    
                    return (
                      <div 
                        key={docType.key}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          doc.uploaded 
                            ? 'border-[var(--success)] bg-[var(--success)]/10' 
                            : 'border-[var(--border-primary)] bg-[var(--background-paper)]'
                        }`}
                        data-testid={`doc-upload-${docType.key}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <DocIcon className={`w-5 h-5 ${doc.uploaded ? 'text-[var(--success)]' : 'text-[var(--primary)]'}`} />
                            <div>
                              <h4 className="font-semibold">{docType.label}</h4>
                              <p className="text-xs text-[var(--text-muted)]">
                                {docType.required ? 'Required' : 'Optional'}
                              </p>
                            </div>
                          </div>
                          {doc.uploaded && (
                            <span className="flex items-center gap-1 text-[var(--success)] text-sm font-medium">
                              <Check className="w-4 h-4" />
                              Uploaded
                            </span>
                          )}
                        </div>

                        {!doc.uploaded ? (
                          <div className="space-y-3">
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <Label className="text-xs">Select File</Label>
                                <Input
                                  type="file"
                                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                                  onChange={(e) => handleDocumentSelect(docType.key, e)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="input-styled mt-1 text-sm file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-[var(--primary)] file:text-black"
                                  data-testid={`file-input-${docType.key}`}
                                />
                              </div>
                              <div className="w-40">
                                <Label className="text-xs">Expiration Date</Label>
                                <Input
                                  type="date"
                                  value={doc.expiration_date}
                                  onChange={(e) => handleExpirationChange(docType.key, e.target.value)}
                                  className="input-styled mt-1 text-sm"
                                  min={new Date().toISOString().split('T')[0]}
                                  data-testid={`exp-date-${docType.key}`}
                                />
                              </div>
                            </div>
                            
                            {doc.file && (
                              <div className="flex items-center justify-between p-2 bg-[var(--background-subtle)] rounded-lg">
                                <span className="text-sm text-[var(--text-secondary)] truncate max-w-[200px]">
                                  {doc.file.name}
                                </span>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => uploadDocument(docType.key)}
                                  disabled={uploadingDoc === docType.key || !doc.expiration_date}
                                  className="btn-primary text-sm"
                                  data-testid={`upload-btn-${docType.key}`}
                                >
                                  {uploadingDoc === docType.key ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-4 h-4 mr-1" />
                                      Upload
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-2 bg-[var(--success)]/15 rounded-lg">
                            <div className="text-sm">
                              <p className="font-medium text-[var(--success)]">{doc.file?.name || 'Document uploaded'}</p>
                              <p className="text-[var(--success)] text-xs">Expires: {doc.expiration_date}</p>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeDocument(docType.key)}
                              className="text-[var(--error)] hover:text-red-700 hover:bg-[var(--error)]/10"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Upload Progress Summary */}
                <div className="p-4 bg-[var(--background-subtle)] rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Upload Progress</span>
                    <span className="text-sm text-[var(--text-muted)]">
                      {Object.values(documents).filter(d => d.uploaded).length} / 5 documents
                      {documents.id_document?.uploaded && <span className="text-[var(--success)] ml-2">✓ Ready to continue</span>}
                    </span>
                  </div>
                  <div className="w-full bg-[var(--accent)] rounded-full h-2">
                    <div 
                      className="bg-[var(--primary)] h-2 rounded-full transition-all"
                      style={{ width: `${(Object.values(documents).filter(d => d.uploaded).length / 5) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1">
                    Back
                  </Button>
                  <Button 
                    type="button" 
                    onClick={() => setStep(4)} 
                    className="btn-primary flex-1" 
                    disabled={!areAllDocumentsUploaded()}
                    data-testid="next-step-3"
                  >
                    Continue <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Availability & References */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
                  <Calendar className="w-5 h-5 text-[var(--primary)]" />
                  Availability
                </h2>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="text-left p-2">Day</th>
                        {timeSlots.map(slot => (
                          <th key={slot} className="p-2 capitalize">{slot}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {days.map(day => (
                        <tr key={day} className="border-t border-[var(--border-primary)]">
                          <td className="p-2 capitalize font-medium">{day}</td>
                          {timeSlots.map(slot => (
                            <td key={slot} className="p-2 text-center">
                              <button
                                type="button"
                                onClick={() => handleAvailabilityToggle(day, slot)}
                                className={`w-8 h-8 rounded-lg transition-all ${
                                  formData.availability[day].includes(slot)
                                    ? 'bg-[var(--primary)] text-white'
                                    : 'bg-[var(--accent)]'
                                }`}
                              >
                                {formData.availability[day].includes(slot) && <Check className="w-4 h-4 mx-auto" />}
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h2 className="text-xl font-bold flex items-center gap-2 mt-8 mb-4">
                  <Users className="w-5 h-5 text-[var(--primary)]" />
                  References (2 Required)
                </h2>

                {formData.references.map((ref, index) => (
                  <div key={`ref-${index}-${ref.name || 'empty'}`} className="p-4 bg-[var(--background-subtle)] rounded-xl space-y-3">
                    <h4 className="font-semibold">Reference {index + 1}</h4>
                    <div className="grid md:grid-cols-3 gap-3">
                      <Input
                        placeholder="Full Name"
                        value={ref.name}
                        onChange={(e) => handleReferenceChange(index, 'name', e.target.value)}
                        className="input-styled"
                      />
                      <Input
                        placeholder="Phone Number"
                        value={ref.phone}
                        onChange={(e) => handleReferenceChange(index, 'phone', e.target.value)}
                        className="input-styled"
                      />
                      <Input
                        placeholder="Relationship"
                        value={ref.relationship}
                        onChange={(e) => handleReferenceChange(index, 'relationship', e.target.value)}
                        className="input-styled"
                      />
                    </div>
                  </div>
                ))}

                <div className="flex gap-4">
                  <Button type="button" onClick={() => setStep(3)} className="btn-secondary flex-1">
                    Back
                  </Button>
                  <Button type="button" onClick={() => setStep(5)} className="btn-primary flex-1" data-testid="next-step-4">
                    Continue <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 5: Emergency Contact & Submit */}
            {step === 5 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-bold flex items-center gap-2 mb-6">
                  <Phone className="w-5 h-5 text-[var(--primary)]" />
                  Emergency Contact
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Emergency Contact Name *</Label>
                    <Input
                      name="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                      data-testid="app-emergency-name"
                    />
                  </div>
                  <div>
                    <Label>Emergency Contact Phone *</Label>
                    <Input
                      name="emergency_contact_phone"
                      type="tel"
                      value={formData.emergency_contact_phone}
                      onChange={handleChange}
                      className="input-styled mt-1"
                      required
                      data-testid="app-emergency-phone"
                    />
                  </div>
                </div>

                {/* Document Summary */}
                <div className="p-4 bg-[var(--success)]/10 border border-[var(--success)]/30 rounded-xl">
                  <h3 className="font-bold flex items-center gap-2 mb-3 text-[var(--success)]">
                    <Check className="w-5 h-5" />
                    Documents Uploaded
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(documents).map(([key, doc]) => (
                      <div key={key} className="flex items-center gap-2 text-green-700">
                        <Check className="w-4 h-4" />
                        <span>{getDocumentLabel(key)}</span>
                        <span className="text-[var(--success)] text-xs">
                          (Exp: {doc.expiration_date})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6 bg-[var(--accent)] rounded-xl">
                  <h3 className="font-bold flex items-center gap-2 mb-4">
                    <FileCheck className="w-5 h-5 text-[var(--primary)]" />
                    Background Check Consent
                  </h3>
                  <p className="text-sm text-[var(--text-secondary)] mb-4">
                    By checking the box below, you authorize NC Help Hub to conduct a background check 
                    as part of your application process. This may include criminal history, employment 
                    verification, and reference checks.
                  </p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      id="background_check_consent"
                      checked={formData.background_check_consent}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, background_check_consent: checked }))}
                      data-testid="app-consent-checkbox"
                    />
                    <span className="text-sm">
                      I consent to a background check and certify that all information provided is accurate and complete.
                    </span>
                  </label>
                </div>

                <div className="flex gap-4">
                  <Button type="button" onClick={() => setStep(4)} className="btn-secondary flex-1">
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading || !formData.background_check_consent || !areAllDocumentsUploaded()}
                    className="btn-primary flex-1"
                    data-testid="submit-application"
                  >
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
              </motion.div>
            )}
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-[var(--text-muted)]">
              Already have an account?{' '}
              <Link to="/login" className="text-[var(--primary)] font-semibold hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WorkerApplication;
